import React, { useRef, useState } from 'react';
import {
    FileUpload,
    FileUploadHeaderTemplateOptions,
    FileUploadSelectEvent,
    ItemTemplateOptions,
} from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { setSidebar, setToast } from '../../../../app/appSlice';
import { useAppDispatch } from '../../../../hooks/hooks';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormFields } from '../../../treatments/NewTopologyForm';
import { useImportFunctionMutation, useUpdateNodeMutation } from '../../DataLibrarySlice';
import { LabelsSelectors, SelectedLabels, useLabelsSelectors } from '@kosmos/shared';

export default function ImportFunction({onUploadDone, functionName, metadatas}: {onUploadDone: () => void, functionName?:string, metadatas?: SelectedLabels}) {
    const [selectedFile, setSelectedFile] = useState<File>();
    const fileUploadRef = useRef<FileUpload>(null);
    const dispatch = useAppDispatch();
    const [importFunction] = useImportFunctionMutation();
    const [updateFunction] = useUpdateNodeMutation();
    const {t: translate} = useTranslation();
    const { selectedLabels, validate: validateLabels } = useLabelsSelectors();

    const {
        handleSubmit,
    } = useForm<FormFields>({ mode: 'onSubmit', reValidateMode: 'onChange' });

    const clearImport = () => {
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };
    const onSelect = (e: FileUploadSelectEvent) => {
        setSelectedFile(e.files[0]);
    };

    const uploadNewFunction = async (reader: FileReader, data: FormFields) => {
        if (!validateLabels()) {
            return;
        }
        reader.onload = async (e) => {
            if (!functionName) {
                try {
                    // @ts-ignore
                    await importFunction({yaml: e.currentTarget.result, metadatas: selectedLabels}).unwrap();
                    dispatch(setSidebar());
                    dispatch(setToast({
                        severity: "success",
                        content: translate('catalog_functions.success.content'),
                        life: 5_000
                    }));
                    onUploadDone();
                } catch (error: any) {
                    let errorMsg = translate('catalog_functions.error.failed');
                    if (error.status === 406 || error.status === 409) {
                        errorMsg = error.data.message ?? translate('catalog_functions.error.parsing');
                    }
                    dispatch(setToast({
                        severity: "error",
                        content: errorMsg + " " + (error.data?.message ?? ''),
                        life: 8_000
                    }));
                }
            }
            else {
                const regex = new RegExp(`name:\\s*${functionName}\\n`)

                // @ts-ignore
                if (regex.test(e.currentTarget.result.replaceAll('\r', '')) ){
                    try {
                        // @ts-ignore
                        await updateFunction({yaml: e.currentTarget.result, metadatas: selectedLabels}).unwrap();
                        dispatch(setSidebar());
                        dispatch(setToast({
                            severity: "success",
                            title: translate('catalog_functions.success.title'),
                            content: translate('catalog_functions.success.content'),
                            life: 5_000
                        }));
                        onUploadDone();
                    } catch (error: any){
                        dispatch(setToast({
                            severity: "error",
                            title: translate('catalog_functions.error.title'),
                            content: translate('catalog_functions.error.failed'),
                            life: 10_000
                        }));
                    }
                }
                else {
                    dispatch(setToast({
                        severity: "error",
                        title: translate('catalog_functions.error.title'),
                        content: translate('catalog_functions.error.bad_name'),
                        life: 10_000
                    }));
                }
            }
        };
    }

    const onSubmit = async (data: FormFields) => {
        const reader = new FileReader();
        reader.readAsText(selectedFile!);
        await uploadNewFunction(reader, data);
    };

    const onTemplateRemove = (callback: Function) => {
        setSelectedFile(undefined);
        clearImport();
        callback();
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const { className, chooseButton} = options;
        const totalSize = selectedFile ? selectedFile.size : 0;
        const value = totalSize / 10000;
        const formattedValue = fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
                <div className="flex align-items-center gap-3 ml-auto">
                    <span>{formattedValue} / 1 MB</span>
                    <ProgressBar value={value} showValue={false} style={{ width: '5rem', height: '12px' }}></ProgressBar>
                </div>
            </div>
        );
    };

    const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {
        const file = inFile as File;
        return (
            <div className="flex align-items-center flex-wrap">
                <div className="flex align-items-center" style={{ width: '40%' }}>
                    <span className="flex flex-column align-items-center text-left ml-3">
                        {file.name}
                        <small>{new Date(file.lastModified).toLocaleString()}</small>
                    </span>
                </div>
                <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => onTemplateRemove(props.onRemove)} />
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column">
                <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    {translate('catalog_functions.dragAndDrop')}
                </span>
            </div>
        );
    };

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined' };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid mt-3">
            <Tooltip target=".custom-choose-btn" content={translate('catalog_functions.choose')} position="bottom"/>
            <Tooltip target=".custom-upload-btn" content={translate('catalog_functions.upload')} position="bottom"/>

            <FileUpload ref={fileUploadRef} name="importFunction" customUpload accept=".yml,.yaml" maxFileSize={1000000}
                        headerTemplate={headerTemplate} itemTemplate={itemTemplate} emptyTemplate={emptyTemplate}
                        onSelect={onSelect} chooseOptions={chooseOptions}/>

            <div className="p-field mt-4">
                <LabelsSelectors values={metadatas} />
            </div>

            <div className="flex mb-2 py-3">
                <div className="flex-grow-1 mr-1">
                    <Button
                        label={translate("button.cancel")}
                        onClick={() => {
                            dispatch(setSidebar());
                        }}
                        className="p-button-text"
                    />
                </div>
                <div className="flex-grow-1">
                    <Button
                        type="submit" disabled={!selectedFile}
                        label={translate("button.submit")}
                    />
                </div>
            </div>
        </form>
    )
}
